<script>
import _ from 'lodash'

import mixin from '@/mixins/mixin.vue'
import userMixin from '@/mixins/userMixin.vue'
import { mapGetters } from 'vuex'
import {
  finishDeferredInteraction as resolveDeferredInteractionResult,
  startDeferredInteraction as createDeferredInteractionState
} from '@/components/Filters/deferredInteraction'

export default {
  props: {
    required: Boolean,
    disabled: Boolean,
    isLoading: Boolean,
    emptyStateIcon: String,
    emptyStateTitle: String,
    emptyStateMessage: String,
    showEmptyState: {
      type: Boolean,
      default: false,
    },
    selectedAccountIds: {
      type: [Array, String],
      required: false,
      default: () => [],
    },
    availableAccounts: {
      type: Array,
      default: () => [],
    },
    availableTeams: {
      type: Array,
      default: () => [],
    },
    initialAssignees: {
      type: Array,
      default: () => [],
    },
    limit: { type: Number, default: null },
    label: {
      type: String,
      required: false,
    },
    validationRules: {
      type: Array,
      required: false,
    },
    multiple: {
      type: Boolean,
      required: false,
    },
    hideDetails: { type: Boolean, required: false, default: false },
    includeCurrentAccountAtTop: {
      type: Boolean,
      default: false,
    },
    emitAutoApplyMetadata: Boolean,
  },

  mixins: [mixin, userMixin],

  data() {
    return {
      interactionSequence: 0,
      menuCurrentValue: null,
      menuInitialValue: null,
      menuInteractionId: null,
      searchTerm: '',
      cachedAccounts: {},
      teamsAccounts: [],
    }
  },

  mounted() {
    this.addAccountsToCache(this.initialAssignees)
  },

  computed: {
    ...mapGetters(['account']),

    labelText() {
      return this.label ?? this.$t('common.assignee_other')
    },

    isSearching() {
      return _.isEmpty(this.searchTerm)
    },

    selectedAccounts() {
      if (!this.multiple) {
        return [this.cachedAccounts[this.selectedAccountIds]]
      }

      return (
        this.selectedAccountIds?.map(
          accountId => this.cachedAccounts[accountId]
        ) ?? []
      )
    },

    inputItems() {
      const accounts = _.uniqBy(
        [...this.availableAccounts, ...this.selectedAccounts],
        'id'
      )

      const currentAccountItem = this.includeCurrentAccountAtTop
        ? this.parseAccounts([this.account])
        : []

      const filteredAccounts = _.compact(accounts).filter(
        acc => acc.id !== this.account.id
      )

      const sortedAccounts = _.orderBy(
        this.parseAccounts(filteredAccounts),
        item => item.title.toLowerCase(),
        'asc'
      )

      const sortedTeams = _.orderBy(
        this.parseTeams(this.availableTeams),
        item => item.title.toLowerCase(),
        'asc'
      )

      return [...currentAccountItem, ...sortedAccounts, ...sortedTeams]
    },

    rules() {
      const rules = []
      if (this.required)
        rules.push(
          items => items.length > 0 || this.$t('routines.min_assignees_error')
        )
      if (this.limit)
        rules.push(
          items =>
            items.length <= this.limit ||
            this.$t('routines.maximum_assignees', {
              maxAssignees: this.$constants.routines.assignees.maxLength,
            })
        )
      return rules
    },
  },

  methods: {
    buildAssigneeChangePayload(value, overrides = {}) {
      if (!this.emitAutoApplyMetadata) return value

      return {
        value,
        ...overrides,
      }
    },

    emitAssigneeChange(value, overrides = {}) {
      this.$emit('change', this.buildAssigneeChangePayload(value, overrides))
    },

    emitLegacyAssigneeChange(value) {
      this.$emit('change', value)
    },

    emitInstantAssigneeChange(value) {
      if (!this.emitAutoApplyMetadata) {
        return this.emitLegacyAssigneeChange(value)
      }

      return this.emitAssigneeChange(value, {
        commitMode: 'instant',
        source: 'user',
        shouldApply: true,
      })
    },

    shouldDeferAssigneeSelection(items) {
      return (
        !this.isInstantAssigneeSelection(items) && this.emitAutoApplyMetadata
      )
    },

    ensureDeferredAssigneeInteraction() {
      if (!this.menuInteractionId) this.startDeferredAssigneeInteraction()
    },

    startDeferredAssigneeInteraction() {
      const deferredState = createDeferredInteractionState({
        interactionSequence: this.interactionSequence,
        prefix: 'assignees',
        value: this.selectedAccountIds ?? [],
      })

      this.interactionSequence = deferredState.interactionSequence
      this.menuCurrentValue = deferredState.menuCurrentValue
      this.menuInitialValue = deferredState.menuInitialValue
      this.menuInteractionId = deferredState.menuInteractionId
    },

    resetDeferredAssigneeInteraction() {
      this.menuCurrentValue = null
      this.menuInitialValue = null
      this.menuInteractionId = null
    },

    emitDeferredAssigneeDraft(value) {
      // Emits intermediate multiselect changes while the menu interaction is
      // still in progress, so the parent can sync local state without applying.
      this.ensureDeferredAssigneeInteraction()
      this.menuCurrentValue = globalThis.structuredClone(value)

      return this.emitAssigneeChange(value, {
        commitMode: 'deferredAfterBlur',
        source: 'user',
        shouldApply: false,
        interactionId: this.menuInteractionId,
      })
    },

    emitDeferredAssigneeCommit(value) {
      // Emits the final value for the same interaction after blur/close, which
      // is the signal that the deferred selection is ready to auto-apply.
      return this.emitAssigneeChange(value, {
        commitMode: 'deferredAfterBlur',
        source: 'user',
        shouldApply: true,
        interactionId: this.menuInteractionId,
      })
    },

    finishDeferredAssigneeInteraction() {
      if (!this.emitAutoApplyMetadata || !this.menuInteractionId) return

      const { didChange, finalValue } = resolveDeferredInteractionResult({
        fallbackValue: this.selectedAccountIds ?? [],
        menuCurrentValue: this.menuCurrentValue,
        menuInitialValue: this.menuInitialValue,
      })

      if (!didChange) {
        this.resetDeferredAssigneeInteraction()
        return
      }

      this.emitDeferredAssigneeCommit(finalValue)

      this.resetDeferredAssigneeInteraction()
    },

    handleAssigneeMenuStateChange(isOpen) {
      if (!this.multiple || !this.emitAutoApplyMetadata) return

      if (isOpen) {
        this.startDeferredAssigneeInteraction()
        return
      }

      this.finishDeferredAssigneeInteraction()
    },

    isInstantAssigneeSelection(items) {
      return _.isEmpty(items) || !this.multiple
    },

    getAssigneeIdsForSelectionItem(item) {
      if (item?.isTeam) return this.getAccountIdsFromTeam(item)

      return [item?._id ?? item]
    },

    normalizeSelectedAssigneeIds(items = []) {
      const selectedItems = [...items]
      const lastItem = selectedItems.pop()

      return _.uniq([
        ...selectedItems,
        ...this.getAssigneeIdsForSelectionItem(lastItem),
      ])
    },

    getRemainingAssigneeIds(accountToRemove) {
      return this.selectedAccountIds.filter(
        account => account !== accountToRemove.id
      )
    },

    handleChange(items) {
      this.searchTerm = ''

      if (this.isInstantAssigneeSelection(items)) {
        return this.emitInstantAssigneeChange(items)
      }

      const selectedAssigneeIds = this.normalizeSelectedAssigneeIds(items)

      if (!this.shouldDeferAssigneeSelection(items)) {
        return this.emitLegacyAssigneeChange(selectedAssigneeIds)
      }

      return this.emitDeferredAssigneeDraft(selectedAssigneeIds)
    },

    getAccountIdsFromTeam(team) {
      return [
        ...team.leaders?.map(leader => leader.id),
        ...team.members?.map(member => member.id),
      ]
    },

    handleRemove(accountToRemove) {
      if (!this.multiple) {
        return this.emitInstantAssigneeChange(null)
      }

      const nextAccounts = this.getRemainingAssigneeIds(accountToRemove)

      if (!this.emitAutoApplyMetadata) {
        return this.emitLegacyAssigneeChange(nextAccounts)
      }

      return this.emitDeferredAssigneeDraft(nextAccounts)
    },

    parseAccounts(accounts) {
      if (!accounts) return

      return accounts?.map?.(account => ({
        ...account,
        id: account?._id ?? account?.id,
        photo: account?.photo,
        title: this.getFullName(account),
        subtitle: account?.position,
      }))
    },

    parseTeams(teams) {
      if (!teams) return

      return teams?.map?.(team => {
        const totalMembers = this.getTeamTotalMembers(team)

        return {
          ...team,
          isTeam: true,
          title: team.name,
          subtitle: `${totalMembers} ${this.$t('common.member', {
            count: totalMembers,
          })}`,
        }
      })
    },

    getTeamTotalMembers(team) {
      return (team?.leaders?.length ?? 0) + (team?.members?.length ?? 0)
    },

    addAccountsToCache(accounts) {
      const parsedNewAccounts = this.parseAccounts(accounts)
      const hashedAccounts = _.keyBy(parsedNewAccounts, 'id')
      this.cachedAccounts = { ...this.cachedAccounts, ...hashedAccounts }
    },
  },

  watch: {
    searchTerm: _.debounce(function(term) {
      this.$emit('search', term)
    }, 1000),

    availableAccounts(newAccounts) {
      this.addAccountsToCache(newAccounts)
    },

    availableTeams(newTeams) {
      const teamAccounts = newTeams.reduce((result, team) => {
        return [...result, ...team.leaders, ...team.members]
      }, [])

      this.addAccountsToCache(teamAccounts)
    },

    initialAssignees(newAccounts) {
      this.addAccountsToCache(newAccounts)
    },
  },
}
</script>
